import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { links } from '@/config/navbar';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconChevronRight } from '@tabler/icons-react';
import prepareTranslate from '@/helpers/dictionary';
import {
  Button, Center, Flex, Loader, Text,
} from '@mantine/core';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { useNavbar } from '@/hooks/NavbarContext';
import MenuGroups from '../MenuGroups';
import styles from './style.module.css';
import SectionExtra from '../SectionExtra';
import Panel from './Panel';
import Navbar from '../Navbar';
import PageHead from '../PageHead';

/*
menuGroups example:
[
    {
      links: [
        {
          id: 'details',
          title: 'פרטים',
          isDefault: true,
        },
        {
          id: 'contracts',
          title: 'הסכמים',
        },
      ],
    },
  ]
*/

/*
tabs example:
[
  {
    title: 'comments',
    content: <Comments type="agent" id={id} showForm />,
    isDefault: true,
  },
  {
    title: 'agent histories',
    content: <Histories type="agent" id={id} />,
  }
]
*/

/*
actions example:
[
  <Button key="update">{t('global.update')}</Button>,
  <Button key="cancel" variant="outline">{t('global.cancel')}</Button>,
]
*/

/**
 * get default active name according to data
 * @param {array} data menuGroups
 * @returns {string}
 */
const getMenuGroupsDefaultActive = (data) => {
  let defaultActive = null;
  if (data.length) {
    let defaultPanel = null;

    data.forEach((menu) => {
      const { links: menuLinks = [] } = menu;
      const item = menuLinks.find((link) => link.isDefault);

      if (!defaultPanel && item) {
        defaultPanel = item;
      }
    });

    defaultActive = (defaultPanel) ? defaultPanel.id : data[0]?.links[0]?.id;
  }

  return defaultActive;
};
const PageTemplate = (props) => {
  const {
    onPanelClick = () => {},
    menuGroups = [],
    side,
    tabsTitle,
    actions = [],
    tabs = [],
    children,
    title,
    subtitle,
    headerActions,
    loading,
    withBackButton = true,
    showTitlesInHeader = false,
    information,
  } = props;

  const { id, tab } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const isShowSide = false;
  const { isExpanded: isNavbarExpanded } = useNavbar();

  const defaultActive = getMenuGroupsDefaultActive(menuGroups);
  const isInvalidTab = tab === ':tab';
  const activeTab = isInvalidTab ? defaultActive : tab;
  const t = prepareTranslate();
  const url = location.pathname;
  const onMenuGroupsPanelClick = (ide) => {
    const newPath = url.replace(`/${tab}`, `/${ide}`);
    onPanelClick(ide);
    navigate(newPath, { replace: true });
  };

  const newPath = url.replace(`/${tab}`, `/${activeTab}`);

  if (newPath !== url) {
    navigate(newPath, { replace: true });
  }

  // TODO: find "react" way to show / hide panels
  useEffect(() => {
    const allPanels = document.querySelectorAll('[data-panel]');

    if (menuGroups.length) {
      const element = document.querySelector(`[data-panel=${activeTab}]`);
      allPanels.forEach((e) => {
        e.style.display = 'none';
      });

      if (element) {
        element.style.display = 'block';
      }
    } else {
      allPanels.forEach((e) => {
        e.style.display = 'block';
      });
    }
  }, [tab, menuGroups, defaultActive, navigate, id, activeTab, url]);

  const { goBack } = useNavigationHistory();

  return (
    <div className={styles.container}>
   {showTitlesInHeader ? <PageHead title={title} subtitle={subtitle} /> : <PageHead />}
      <div className={styles.wrapper}>
        <Navbar links={links} />
        <div className={`${styles.contentContainer} ${isNavbarExpanded ? styles.expanded : ''}`}>
          <div className={styles.content} data-active={tab}>
            <div className={styles.headerContent}>
              <div className={styles.headerButtons}>
                {withBackButton && <Button radius={'2rem'} onClick={goBack} variant='outline' leftSection={<IconChevronRight stroke={2}/>} >{t('global.back')}</Button> }
                {headerActions?.length > 0
                  && <div className={styles.actions}> {headerActions}</div>
                }
              </div>
        <header className={styles.info}>
        <div className={styles.headerRow}>
                  {title
                    && !showTitlesInHeader
                    && <h2 className={styles.title}>{title}</h2>
                  }
                  {actions.length > 0
                    && <div className={styles.actions}> {actions} </div>
                  }
           </div>
            {!showTitlesInHeader && Array.isArray(subtitle)
              ? subtitle.map((text, index) => <Flex key={index}><Text size='sm' c='#6B6E77'>{text}</Text></Flex>)
              : !showTitlesInHeader && <Flex><Text size='sm' c='#6B6E77'>{subtitle}</Text></Flex>
                }
                {information}
            {(menuGroups && menuGroups.length > 0) && (
              <div className={styles.menu}>
                <MenuGroups
                  data={menuGroups}
                  active={activeTab}
                  onClick={onMenuGroupsPanelClick}
                />
              </div>
            )}
        </header>
            </div>
            {loading ? (
              <Center>
                <Loader />
              </Center>
            ) : (
              <div className={styles.children} >
                {children}
              </div>
            )}
          </div>
          {isShowSide && (
            <div className={styles.side}>
              {(tabs.length > 0) && <SectionExtra tabs={tabs} title={tabsTitle} />}
              {side}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PageTemplate.Panel = Panel;

PageTemplate.propTypes = {
  onPanelClick: PropTypes.func,
  menuGroups: PropTypes.array,
  side: PropTypes.any,
  views: PropTypes.array,
  actionsTitle: PropTypes.string,
  actions: PropTypes.array,
  tabsTitle: PropTypes.string,
  tabs: PropTypes.array,
  comments: PropTypes.object,
  history: PropTypes.object,
  children: PropTypes.any,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  headerActions: PropTypes.array,
  loading: PropTypes.bool,
  withBackButton: PropTypes.bool,
  showTitlesInHeader: PropTypes.bool,
  information: PropTypes.func,
};

export default PageTemplate;
